import {
    Box,
    Button,
    HStack,
    Progress,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTestOrder } from '../../../api';
import TitleHeader from '../../../components/TitleHeader';
import { hasPermission } from '../../../Utils';

import BillingAndInsuranceSection from './sections/BillingAndInsuranceSection';
import FacilityAndPhysicianSection from './sections/FacilityAndPhysicianSection';
import PatientDemographicsSection from './sections/PatientDemographicsSection';
import RequestedTestsAndPanelsSection from './sections/RequestedTestsAndPanelsSection';

const steps = [
    { id: 1, label: 'Facility & Patient' },
    { id: 2, label: 'Tests & Panels' },
    { id: 3, label: 'Billing & Insurance' },
];

const CreateTestOrderPage = () => {
    const [formData, setFormData] = useState({});
    const [step, setStep] = useState(1);
    const toast = useToast();
    const navigate = useNavigate();

    const nextStep = () => setStep(step + 1);
    const prevStep = () => setStep(step - 1);

    const handleSubmit = async () => {
        try {
            const fd = new FormData();
            Object.entries(formData).forEach(([key, val]) => {
                if (val !== undefined && val !== null) {
                    fd.append(key, typeof val === 'object' ? JSON.stringify(val) : val);
                }
            });

            const res = await createTestOrder(fd);
            if (res.success) {
                toast({ title: 'Success', description: 'Test Order created', status: 'success' });
                navigate('/reqs/test-orders');
            } else {
                toast({ title: 'Error', description: res.data?.message || 'Failed to create test order', status: 'error' });
            }
        } catch (err) {
            console.error(err);
            toast({ title: 'Error', description: 'Failed to create test order', status: 'error' });
        }
    };

    return (
        <Box mx="auto" p={8}>
            <VStack spacing={6} align="stretch">
                <TitleHeader title="Create Test Order" />

                {/* Stepper Header */}
                <Box>
                    <HStack justify="space-between" mb={2}>
                        {steps.map((s) => (
                            <Text
                                key={s.id}
                                fontWeight={s.id === step ? 'bold' : 'normal'}
                                color={s.id === step ? 'blue.600' : 'gray.500'}
                            >
                                {s.label}
                            </Text>
                        ))}
                    </HStack>
                    <Progress
                        value={(step / steps.length) * 100}
                        size="sm"
                        colorScheme="blue"
                        borderRadius="md"
                    />
                </Box>

                {/* Step Content */}
                {step === 1 && (
                    <>
                        <FacilityAndPhysicianSection formData={formData} setFormData={setFormData} />
                        <PatientDemographicsSection formData={formData} setFormData={setFormData} />
                    </>
                )}

                {step === 2 && (
                    <RequestedTestsAndPanelsSection formData={formData} setFormData={setFormData} />
                )}

                {step === 3 && (
                    <BillingAndInsuranceSection formData={formData} setFormData={setFormData} />
                )}

                {/* Navigation */}
                <HStack justify="space-between">
                    {step > 1 && (
                        <Button variant="outline" onClick={prevStep}>
                            Back
                        </Button>
                    )}

                    {step < steps.length ? (
                        <Button colorScheme="blue" onClick={nextStep}>
                            Next
                        </Button>
                    ) : (
                        <HStack spacing={4}>
                            {hasPermission('sendPatientLink') && (
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        setFormData({ ...formData, sendPatientLink: !formData.sendPatientLink })
                                    }
                                >
                                    {formData.sendPatientLink ? '✓ Send Patient Link' : 'Send Patient Link'}
                                </Button>
                            )}
                            <Button colorScheme="teal" onClick={handleSubmit}>
                                Submit Order
                            </Button>
                        </HStack>
                    )}
                </HStack>
            </VStack>
        </Box>
    );
};

export default CreateTestOrderPage;
